/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC } from "react"
import { Tooltip, Tag, Row, Col } from "@duxton-web/ui"
import { ClockOutlined } from "@duxton-web/icons"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { GroupedServiceHour, SellingTime } from "types/models/menu"
import {
  useTranslatedGroupedServiceHours,
  getHasPeriod,
  getIsAlwaysAvailable,
} from "utils/selling_time_helper"
import useSellingTimePeriodRangeText from "hooks/useSellingTimePeriodRangeText"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { convertDateTZ } from "utils/date_formatter"

interface SellingTimeTagProps {
  sellingTime?: SellingTime
}

export const RenderTag: FC<SellingTimeTagProps> = ({
  sellingTime,
}: SellingTimeTagProps) => {
  const {
    menuGroupDetail: { timeZone: menuGroupTimezone },
    outletV2,
  } = useSelector((state: AppState) => state.menu)

  const outletTimezone = outletV2?.timezone
  const timezone = menuGroupTimezone || outletTimezone

  const textMap = useTranslationsMapTyped({
    alwaysAvailable: "menu.sellingTime.dropdown.alwaysAvailable",
    inactive: "menu.sellingTime.tag.inactive",
    ended: "menu.sellingTime.tag.ended",
  })

  const isAlwaysAvailable = getIsAlwaysAvailable(sellingTime)

  const sellingTimeName = isAlwaysAvailable
    ? textMap.alwaysAvailable
    : sellingTime?.name || ""

  const hasPeriod = getHasPeriod(sellingTime)

  const getPeriodText = () => {
    if (sellingTime && hasPeriod && timezone) {
      const startTime = new Date(sellingTime.startTime)
      const endTime = new Date(sellingTime.endTime)
      const startDate = convertDateTZ(startTime, timezone)
      const endDate = convertDateTZ(endTime, timezone)

      if (startDate.isAfter()) {
        return textMap.inactive.replace("<schedule.name> ", "")
      }
      if (endDate.isBefore()) {
        return textMap.ended.replace("<schedule.name> ", "")
      }
    }
    return ""
  }

  const periodText = getPeriodText()

  return (
    <Tag
      size="xsmall"
      css={css`
        margin: 0;
        max-width: 150px;
        transition-duration: 0.1s;
        :hover {
          background-color: #dee5f0;
          max-width: 180px;
        }
        * {
          font-size: 12px;
        }
      `}
    >
      <Row align="middle" wrap={false} gutter={4}>
        <Col>
          <ClockOutlined />
        </Col>
        <Col
          css={css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          `}
        >
          {sellingTimeName}
        </Col>
        {periodText && <Col>{periodText}</Col>}
      </Row>
    </Tag>
  )
}

export const SellingTimeTag: FC<SellingTimeTagProps> = ({
  sellingTime,
}: SellingTimeTagProps) => {
  const periodRangeText = useSellingTimePeriodRangeText(sellingTime)

  const groupedServiceHours = useTranslatedGroupedServiceHours(
    sellingTime?.groupedServiceHours,
    true,
  )

  const textMap = useTranslationsMapTyped({
    periodLabel: "menu.sellingTime.label.period",
  })

  if (!sellingTime) {
    return null
  }

  const renderTooltipText = (groupedServiceHour: GroupedServiceHour) => {
    return (
      <div key={groupedServiceHour.dayRange}>
        <div
          css={css`
            font-weight: 500;
          `}
        >
          {groupedServiceHour.dayRange}
        </div>
        {groupedServiceHour.periods.map(period => {
          return (
            <div key={period}>
              {period}
              <br />
            </div>
          )
        })}
      </div>
    )
  }

  const hasPeriod = getHasPeriod(sellingTime)

  return (
    <Tooltip
      overlay={
        <div
          style={{
            padding: "10px",
          }}
        >
          {groupedServiceHours.map(groupedServiceHour =>
            renderTooltipText(groupedServiceHour),
          )}
          {hasPeriod && (
            <div>
              <br />
              <b>{textMap.periodLabel}</b>
              <br />
              {periodRangeText}
            </div>
          )}
        </div>
      }
      placement="bottom"
    >
      <div>
        <RenderTag sellingTime={sellingTime} />
      </div>
    </Tooltip>
  )
}
